/**
 * PATH: packages/ui/src/components/settings/useAiUsageDashboard.ts
 * FILE_ID: UI-SETTINGS-AIUSAGE-HOOK-001
 * PURPOSE:
 *   All state, effects, fetching, CSV export and derived values for the
 *   AI usage dashboard. Extracted verbatim from AiUsageDashboard.tsx
 *   (god-component split, zero behaviour change) so the component file
 *   stays presentational and under the line limit.
 * WHY:
 *   Separating data flow (this hook) from rendering (the component +
 *   AiUsageEventTable) makes the polling/diff/error logic reviewable in
 *   isolation and keeps the 403-friendly error contract in one place.
 * FLOW:
 *   ┌─────────────┐   ┌────────────────────────┐   ┌───────────────┐
 *   │ props/state │ → │ fetchUsage + poll/diff  │ → │ data + derived │
 *   └─────────────┘   └────────────────────────┘   └───────────────┘
 * DEPENDENCIES:
 *   - react: state/effects/memo/ref
 *   - ./aiUsageFormat: PAGE_SIZE
 *   - ./types: AiUsageApi + response shapes
 * RELATED:
 *   - AiUsageDashboard.tsx: sole consumer (spreads this into the view)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PAGE_SIZE } from './aiUsageFormat'
import type {
  AiUsageApi,
  AiUsageEventRow,
  AiUsageQueryParams,
  AiUsageResponse,
} from './types'

export interface UseAiUsageDashboardArgs {
  organisationId: string | number | null | undefined
  organisationName?: string | null
  api: AiUsageApi
  onCsvError?: (err: unknown) => void
  autoRefreshIntervalMs: number
  autoRefreshDefaultOn: boolean
  onNewActivity?: (rows: AiUsageEventRow[]) => void
}

export function useAiUsageDashboard({
  organisationId,
  organisationName,
  api,
  onCsvError,
  autoRefreshIntervalMs,
  autoRefreshDefaultOn,
  onNewActivity,
}: UseAiUsageDashboardArgs) {
  const [days, setDays] = useState(30)
  const [taskFilter, setTaskFilter] = useState('')
  const [successFilter, setSuccessFilter] = useState<'' | 'true' | 'false'>('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  const [page, setPage] = useState(1)
  const [data, setData] = useState<AiUsageResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [autoRefreshOn, setAutoRefreshOn] = useState(autoRefreshDefaultOn)
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null)
  // Bumped every second so the "X seconds ago" indicator stays
  // current without forcing a refetch.
  const [tickNow, setTickNow] = useState<number>(() => Date.now())

  // Track ids we've already seen so we can compute "new since last
  // fetch" without re-emitting the entire history every poll. The
  // initial fetch primes the set; subsequent polls only emit truly
  // new rows.
  const seenIdsRef = useRef<Set<string>>(new Set())
  const hasPrimedRef = useRef(false)

  const queryParams: AiUsageQueryParams | null = useMemo(() => {
    if (organisationId === null || organisationId === undefined || organisationId === '') {
      return null
    }
    return {
      organisationId,
      days,
      page,
      pageSize: PAGE_SIZE,
      taskType: taskFilter || undefined,
      success: successFilter,
      search: search || undefined,
    }
  }, [organisationId, days, page, taskFilter, successFilter, search])

  /**
   * Centralised fetch. Two modes:
   *   - foreground: shows the spinner and clears any prior error.
   *   - background (silent): used by the auto-refresh poll. We
   *     suppress the spinner so the panel doesn't flicker every
   *     30s, but we still surface errors via `setError`.
   */
  const fetchUsage = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!queryParams) return
      if (!silent) setLoading(true)
      if (!silent) setError(null)
      try {
        const res = await api.fetchUsage(queryParams)
        setData(res)
        setLastFetchedAt(Date.now())

        // Compute "new since last fetch" by diffing against the
        // ids we've recorded on previous successful fetches. We
        // do NOT emit on the very first prime — that would dump
        // a notification per pre-existing row when the user first
        // opens the panel.
        if (onNewActivity) {
          const incoming = res.rows ?? []
          const seen = seenIdsRef.current
          if (hasPrimedRef.current) {
            const fresh = incoming.filter((r) => !seen.has(String(r.id)))
            if (fresh.length > 0) onNewActivity(fresh)
          }
          for (const r of incoming) seen.add(String(r.id))
          hasPrimedRef.current = true
        }
      } catch (e) {
        const status = (e as { response?: { status?: number }; status?: number })
          ?.response?.status ?? (e as { status?: number })?.status
        if (status === 403) {
          setError(
            'AI usage details are visible to organisation owners and admins. Please ask your owner to grant billing access.',
          )
        } else {
          setError('AI usage details are not available yet for this organisation.')
        }
        if (!silent) setData(null)
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [api, queryParams, onNewActivity],
  )

  useEffect(() => {
    void fetchUsage()
  }, [fetchUsage])

  // Background poll — silent refetches at the configured cadence.
  // Skipped when:
  //   - user has toggled auto-refresh off,
  //   - `autoRefreshIntervalMs` is 0 (caller opted out),
  //   - the tab/window is hidden (let the OS save battery).
  useEffect(() => {
    if (!autoRefreshOn) return
    if (!autoRefreshIntervalMs || autoRefreshIntervalMs <= 0) return
    const id = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      void fetchUsage({ silent: true })
    }, autoRefreshIntervalMs)
    return () => window.clearInterval(id)
  }, [autoRefreshOn, autoRefreshIntervalMs, fetchUsage])

  // Tick the "X seconds ago" indicator once per second so it stays
  // current without piggy-backing on a fetch.
  useEffect(() => {
    if (!lastFetchedAt) return
    const id = window.setInterval(() => setTickNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [lastFetchedAt])

  // Reset the "seen" cache whenever the filter window changes — a
  // new query window has a different definition of "new".
  useEffect(() => {
    seenIdsRef.current = new Set()
    hasPrimedRef.current = false
  }, [days, taskFilter, successFilter, search, organisationId])

  useEffect(() => {
    setPage(1)
  }, [days, taskFilter, successFilter, search])

  const lastFetchedLabel = useMemo(() => {
    if (!lastFetchedAt) return ''
    const sec = Math.max(0, Math.round((tickNow - lastFetchedAt) / 1000))
    if (sec < 5) return 'just now'
    if (sec < 60) return `${sec}s ago`
    const min = Math.floor(sec / 60)
    if (min < 60) return `${min}m ago`
    return `${Math.floor(min / 60)}h ago`
  }, [lastFetchedAt, tickNow])

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSearch(searchInput.trim())
  }

  const downloadCsv = useCallback(async () => {
    if (!queryParams) return
    try {
      const blob = await api.downloadCsv(queryParams)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const safeOrg = String(organisationName || organisationId || 'org').replace(
        /[^a-z0-9-]/gi,
        '_',
      )
      a.download = `ai_usage_${safeOrg}_${days}d.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      onCsvError?.(e)
    }
  }, [api, queryParams, organisationName, organisationId, days, onCsvError])

  const summary = data?.summary
  // Memoised so its array identity is stable across renders where `data`
  // is unchanged — keeps the taskOptions useMemo below from recomputing
  // every render (behaviour-identical to the pre-split inline default).
  const breakdown = useMemo(() => data?.breakdown_by_task ?? [], [data])
  const rows = data?.rows ?? []
  const pagination = data?.pagination

  const taskOptions = useMemo(() => {
    const set = new Set<string>()
    breakdown.forEach((b) => set.add(b.task_type))
    return Array.from(set).sort()
  }, [breakdown])

  return {
    // window + filter state
    days,
    setDays,
    taskFilter,
    setTaskFilter,
    successFilter,
    setSuccessFilter,
    search,
    setSearch,
    searchInput,
    setSearchInput,
    page,
    setPage,
    // fetch state
    loading,
    error,
    autoRefreshOn,
    setAutoRefreshOn,
    lastFetchedAt,
    lastFetchedLabel,
    // data + derived
    summary,
    breakdown,
    rows,
    pagination,
    taskOptions,
    // handlers
    fetchUsage,
    handleSearchSubmit,
    downloadCsv,
  }
}

export type UseAiUsageDashboardReturn = ReturnType<typeof useAiUsageDashboard>
